import React from 'react';
import { Navigate } from 'react-router-dom';
import { isValidAdmin, getCurrentUser, clearAuth } from '../utils/auth';
import AdminHeader from './AdminHeader';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ children }) => {
  // Check if user has valid admin access
  if (!isValidAdmin()) {
    const user = getCurrentUser();
    
    // If user exists but is not admin, show unauthorized message
    if (user && user.role !== 'admin') {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl">🚫</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
              <p className="text-gray-600">
                You don't have administrator privileges to access this page.
              </p>
            </div>
            
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Current user: {user.name} ({user.role})
              </p>
              <button
                onClick={() => {
                  clearAuth();
                  window.location.href = '/login';
                }}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
              >
                Login as Admin
              </button>
              <button
                onClick={() => window.history.back()}
                className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    // If no user or invalid token, redirect to login
    clearAuth(); // Clear any invalid auth data
    return <Navigate to="/login" replace />;
  }

  // User has valid admin access, render the protected component with admin header
  return (
    <>
      <AdminHeader />
      {children}
    </>
  );
};

export default ProtectedAdminRoute;
